import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@utils';

const buttonVariants = cva(
  cn(
    'cursor-pointer inline-flex items-center justify-center whitespace-nowrap rounded-lg font-serif font-medium transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-2',
    '[&_[data-icon]]:size-5 [&_[data-icon]]:shrink-0',
    'disabled:bg-gray-200 disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:shadow-none',
    'shadow-sm hover:shadow-md active:shadow-sm',
  ),
  {
    variants: {
      variant: {
        contained: '',
        outlined: 'border-2 bg-transparent',
        ghost: 'bg-transparent border-0 shadow-none hover:shadow-none',
        link: 'bg-transparent border-0 shadow-none hover:shadow-none underline-offset-4 hover:underline p-0',
      },
      color: {
        primary: 'bg-primary',
        secondary: 'bg-secondary',
        tertiary: 'bg-accent',
      },
      size: {
        default: 'px-5 py-3',
        sm: 'p-2',
        lg: 'py-4 px-8',
        icon: 'h-10 w-10 p-0',
      },
    },
    compoundVariants: [
      // Contained Primary (Olive)
      {
        variant: 'contained',
        color: 'primary',
        class: 'bg-primary text-light hover:bg-primary/90 active:bg-primary/80',
      },
      // Contained Secondary (Accent/Dark Olive)
      {
        variant: 'contained',
        color: 'secondary',
        class:
          'bg-secondary text-light backdrop-blur-sm hover:bg-secondary/90 active:bg-secondary/80',
      },
      // Outlined Primary
      {
        variant: 'outlined',
        color: 'primary',
        class: 'border-primary text-primary hover:bg-primary hover:text-light',
      },
      // Outlined Secondary
      {
        variant: 'outlined',
        color: 'secondary',
        class: 'border-accent text-accent hover:bg-accent hover:text-light',
      },
      // Ghost Primary
      {
        variant: 'ghost',
        color: 'primary',
        class: 'text-primary hover:bg-primary/10',
      },
      // Ghost Secondary
      {
        variant: 'ghost',
        color: 'secondary',
        class: 'text-accent hover:bg-accent/10',
      },
      // Link Primary
      {
        variant: 'link',
        color: 'primary',
        class: 'text-primary hover:text-primary/80',
      },
      // Link Secondary
      {
        variant: 'link',
        color: 'secondary',
        class: 'text-accent hover:text-accent/80',
      },
      // Contained Tertiary  (accent = #4f4944 dark charcoal)
      {
        variant: 'contained',
        color: 'tertiary',
        class: 'bg-accent text-light hover:bg-accent/90 active:bg-accent/80',
      },
      // Outlined Tertiary
      {
        variant: 'outlined',
        color: 'tertiary',
        class: 'border-accent text-accent hover:bg-accent hover:text-light',
      },
      // Ghost Tertiary
      {
        variant: 'ghost',
        color: 'tertiary',
        class: 'text-accent hover:bg-accent/10',
      },
      // Link Tertiary
      {
        variant: 'link',
        color: 'tertiary',
        class: 'text-accent hover:text-accent/80',
      },
    ],
    defaultVariants: {
      variant: 'contained',
      color: 'primary',
      size: 'default',
    },
  },
);

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, color, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, color, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export { Button, buttonVariants };
